import { classMerge } from '@components/utilities/classMerge'
import type { BorderColor } from '@lib/colors/colors'
import type { E164Number } from 'libphonenumber-js'
import * as React from 'react'
import { useImperativeHandle, useState } from 'react'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'

function getInputBorderColor(
  isFilledButInvalid: boolean,
  focused: boolean,
  isValid?: boolean,
  neutralBorderColor?: BorderColor,
) {
  if (isFilledButInvalid) {
    return 'border-destructive300'
  }

  if (Boolean(isValid)) {
    return 'border-success300'
  }

  if (focused) {
    return 'border-primary300'
  }

  return neutralBorderColor ?? 'border-neutral300'
}

export type PhoneNumberInputCustomRef = {
  focusInput: () => void
}

// eslint-disable-next-line react/display-name
export const PhoneNumberInput = React.forwardRef<
  PhoneNumberInputCustomRef,
  {
    autoFocus?: boolean
    containerClassName?: string
    name?: string
    neutralBorderColor?: BorderColor
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
    placeholder?: string
    required?: boolean
    setValue: (e: string) => void
    value?: string
  }
>(
  (
    {
      autoFocus,
      containerClassName,
      name,
      neutralBorderColor,
      onKeyDown,
      placeholder = '(123) 456-7890',
      required,
      setValue,
      value,
    },
    ref,
  ) => {
    const [focused, setFocus] = useState(Boolean(autoFocus))
    const inputRef = React.useRef<HTMLInputElement>(null)

    const handleNumberChange = (e?: E164Number) => {
      const isChangeValid = e ? isValidPhoneNumber(e) : false

      isChangeValid
        ? inputRef.current?.setCustomValidity('')
        : inputRef.current?.setCustomValidity(
            'Please enter a valid phone number',
          )

      setValue(e ?? '')
    }

    const isValid = isValidPhoneNumber(value ?? '')
    const isFilledButInvalid = (value?.length ?? 0) > 11 && !isValid

    const inputBorderColor = getInputBorderColor(
      isFilledButInvalid,
      focused,
      isValid,
      neutralBorderColor,
    )

    const focusInput = () => {
      inputRef.current?.focus()
      // force the cursor to the end of the input
      inputRef.current?.setSelectionRange(-1, -1)
      setFocus(true)
    }

    useImperativeHandle(ref, () => ({
      focusInput,
    }))

    const classes = classMerge(
      'w-full rounded-md',
      'border-2',
      'h-16 px-6',
      inputBorderColor,
      'shadow-none', // remove default drop shadow style
      containerClassName,
    )

    return (
      <PhoneInput
        autoComplete='tel'
        autoFocus={autoFocus}
        className={classes}
        defaultCountry='US'
        id={name ?? 'phone-input'}
        name={name}
        onBlur={() => setFocus(false)}
        onChange={handleNumberChange}
        onFocus={() => setFocus(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        // @ts-ignore - react-phone-number-input is more specific, but this works fine
        ref={inputRef}
        required={required}
        value={value}
      />
    )
  },
)
